import globalStorePlugin from '~~/common/vue/store/global';
import userStorePlugin from '~~/common/vue/store/user';
import browserStorePlugin from '~~/common/vue/store/browser';
import searchStorePlugin from '~~/search/common/vue/store/search';
import notificationsStorePlugin from '~~/common/vue/components/notifications/store';
import { PAGINATION_DYNAMIC, PAGINATION_STATIC, WEB_PRODUCT_ADS_FULLWIDTH_EXPERIMENT, SHOPPING_BING_EXPERIMENT } from '~/constants';
import { injectResultsAfterResultTypes, createIsResultOfType, splitProductAdsForABTest } from '~/utils';
import { trackPageLoad, trackAdClick } from '~/trackers/dwell-time';
import { PRIVACY_PROXY_MS } from '~~/common/js/constants/feature-flags';

const isProductAds = createIsResultOfType('productAds');
const isAd = createIsResultOfType('ad');

export const plugins = [
  globalStorePlugin,
  userStorePlugin,
  browserStorePlugin,
  searchStorePlugin,
  notificationsStorePlugin,
];

export const state = () => ({
  productAds: [],
  adTracking: null,
  error: null,
  fetchingResults: false,
  fetchError: false,
  mainlineResults: [],
  mainlineAdResultsCount: 0,
  pagination: null,
  queryCorrection: null,
  relatedQueries: null,
  relatedSearches: null,
  sidebarResults: [],
  spellingSuggestion: null,
});

export const getters = {
  dynamicPagination(st) {
    return st.pagination?.type === PAGINATION_DYNAMIC;
  },
  staticPagination(st) {
    return st.pagination?.type === PAGINATION_STATIC;
  },
  results(st) {
    const {
      mainlineResults,
      pagination,
      queryCorrection,
      relatedQueries,
      relatedSearches,
      sidebarResults,
      spellingSuggestion,
    } = st;
    return {
      mainline: mainlineResults[0] ?? [],
      pagination,
      queryCorrection,
      relatedQueries,
      relatedSearches,
      spellingSuggestion,
      sidebar: sidebarResults ?? [],
    };
  },
  hasMainlineResults: (st) => st.mainlineResults?.length > 0,
  hasProductAdsResults: (st) => st.productAds?.length > 0,
  hasProductAds: (
    { mainlineResults, sidebarResults },
    { hasMainlineResults, hasSidebarResults },
  ) => {
    const hasMainlineProductAds = hasMainlineResults ?
      mainlineResults.some((results) => results.some(isProductAds)) : false;
    const hasSidebarProductAds = hasSidebarResults ?
      sidebarResults.some(isProductAds) :
      false;
    return hasMainlineProductAds || hasSidebarProductAds;
  },
  hasShoppingLink: (st, { hasProductAds, hasProductAdsResults }, rootStore, rootGetters) => {
    const isShoppingLinkMarket = rootGetters['search/isShoppingLinkMarket'];
    const flagEnabled = rootGetters['goodall/getExperiment'](SHOPPING_BING_EXPERIMENT) === 'enabled';
    const hasProductAdsOrShoppingIntent = hasProductAds || hasProductAdsResults || rootGetters['search/hasShoppingIntent'];
    return (isShoppingLinkMarket || flagEnabled) && hasProductAdsOrShoppingIntent;
  },
  hasSidebarResults: (st) => st.sidebarResults?.length > 0,
  bingTracking(st) {
    return st.adTracking?.type === 'bingAdTracking' ? st.adTracking : null;
  },
};

export const mutations = {
  addMainlineResults(st, results) {
    st.mainlineResults.push(results);
  },
  setProductAds(st, productAds) {
    st.productAds = productAds;
  },
  setAdTracking(st, adTracking) {
    st.adTracking = adTracking;
  },
  setError(st, error) {
    st.error = error;
  },
  setFetchingResults(st, fetching) {
    st.fetchingResults = fetching;
  },
  setFetchError(st, error) {
    st.fetchError = error;
  },
  setMainlineResults(st, results) {
    st.mainlineResults = results;
  },
  setMainlineAdResultsCount(st, count) {
    st.mainlineAdResultsCount = count;
  },
  setPagination(st, pagination) {
    Object.assign(st, { pagination });
  },
  setQueryCorrection(st, queryCorrection) {
    st.queryCorrection = queryCorrection;
  },
  setRelatedQueries(st, relatedQueries) {
    st.relatedQueries = relatedQueries;
  },
  setRelatedSearches(st, relatedSearches) {
    st.relatedSearches = relatedSearches;
  },
  setSidebarResults(st, results) {
    st.sidebarResults = results;
  },
  setSpellingSuggestion(st, spellingSuggestion) {
    st.spellingSuggestion = spellingSuggestion;
  },
};

export const actions = {
  nuxtServerInit({ commit, dispatch }, context) {
    dispatch('global/nuxtServerInit', context);
    dispatch('user/nuxtServerInit', context);
    dispatch('browser/nuxtServerInit', context);
    dispatch('search/nuxtServerInit', context);
    dispatch('goodall/overrideFromQuery', context.route.query);
    const { req } = context;
    const { results, resultsError } = req;
    if (resultsError) {
      commit('setError', resultsError);
      return;
    }
    if (results) {
      const goodallExperiments = req?.goodall?.map;
      dispatch('populateResults', { results, goodallExperiments });
    }
  },
  populateMainline({ commit }, { results: { mainline, sidebar }, goodallExperiments }) {
    if (!mainline) {
      return;
    }

    const toInject = [
      sidebar?.find(({ type }) => type === 'entity'),
      sidebar?.find(({ type }) => type === 'entityDisambiguation'),
      sidebar?.find(({ type }) => type === 'climatePledgeRating'),
    ].filter((result) => !!result);

    const results = injectResultsAfterResultTypes(
      mainline,
      toInject,
      ['ad', 'productAds'],
    );

    if (results.length > 0) {
      const hasProductAdsFullwidth = goodallExperiments?.[WEB_PRODUCT_ADS_FULLWIDTH_EXPERIMENT] === 'st_ab_907_product-ads-fullwidth-test';
      const groups = splitProductAdsForABTest(results, hasProductAdsFullwidth);
      commit('setProductAds', groups.productAds);
      commit('setMainlineResults', [groups.results]);
      commit('setMainlineAdResultsCount', groups.results.filter(isAd).length);
    }
  },
  populateSidebar({ commit }, { sidebar }) {
    if (sidebar && sidebar.length > 0) {
      commit('setSidebarResults', sidebar);
    }
  },
  populateResults({ commit, dispatch }, { results, goodallExperiments }) {
    const {
      adTracking,
      pagination,
      queryCorrection,
      relatedQueries,
      relatedSearches,
      spellingSuggestion,
    } = results;
    dispatch('populateMainline', { results, goodallExperiments });
    dispatch('populateSidebar', results);
    commit('setPagination', pagination);
    commit('setQueryCorrection', queryCorrection);
    commit('setSpellingSuggestion', spellingSuggestion);
    commit('setAdTracking', adTracking);
    commit('setRelatedQueries', relatedQueries);
    commit('setRelatedSearches', relatedSearches);
  },
  async fetchNextResults({ commit, state: { fetchingResults } }, url) {
    if (fetchingResults) {
      return;
    }
    commit('setFetchError', false);
    commit('setFetchingResults', true);
    try {
      const response = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
      const { mainline, pagination } = await response.json(); // sidebar
      if (mainline && mainline.length > 0) {
        commit('addMainlineResults', mainline);
      }
      commit('setPagination', pagination);
    } catch (err) {
      commit('setFetchError', true);
    }
    commit('setFetchingResults', false);
  },
  trackPageLoad({ getters: { bingTracking }, rootGetters }) {
    const shouldProxy = rootGetters?.['goodall/getExperiment']?.(PRIVACY_PROXY_MS.flag) === PRIVACY_PROXY_MS.enabled;

    if (bingTracking) {
      trackPageLoad(bingTracking.pageLoadPingUrl, shouldProxy);
    }
  },
  trackAdClick({ getters: { bingTracking }, rootGetters }, pingSuffix) {
    const shouldProxy = rootGetters?.['goodall/getExperiment']?.(PRIVACY_PROXY_MS.flag) === PRIVACY_PROXY_MS.enabled;

    if (bingTracking && pingSuffix) {
      trackAdClick(bingTracking.pingUrlBase, pingSuffix, shouldProxy);
    }
  },
};
